/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : Admin 관련 API 모음
 * 버전 : 1.0
 * 파일명 : Admin.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================ 
 * 수정 : 2026-01-30, 강동형 : 홍보관기준 통계 호출에 name검색 기능 추가
 * ================================================================ */

import axios from 'axios'
import { getAuthToken } from './Auth'
import { toast } from 'react-toastify'

const API_URL = process.env.REACT_APP_API_URL

export const getUser = async (status:string, page:number, pageSize:number)=>{
    try{
        const token = getAuthToken()
        const response = await axios.get(`${API_URL}/user`,{
            params:{
              status: status,
              page:page,
              page_size:pageSize
            },
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
        return response.data
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            return response.data
          }
        }
    }
    }

export const getRecentInterestedData = async()=>{
  try{
        const token = getAuthToken()
        const response = await axios.get(`${API_URL}/interested-students-info-years-v2`,{
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
        return response.data
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            return response.data
          }
        }
    }
}

export const getStageByRate = async()=>{
  try{
        const token = getAuthToken()
        const response = await axios.get(`${API_URL}/interested-students-ratio`,{
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
        return response.data
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            return response.data
          }
        }
    }
}

export const postApproveUser = async(user_id:string)=>{
  try{
        const token = getAuthToken()
        const response = await axios.post(`${API_URL}/user/approve/${user_id}`,null,{
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
        return response.data
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            return response.data
          }
        }
    }
}

export const postInactiveUser =async(user_id:string)=>{
  try{
        const token = getAuthToken()
        const response = await axios.post(`${API_URL}/user/inactive/${user_id}`,null,{
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
        return response.data
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            return response.data
          }
        }
    }
}

export const deleteUser =async(delete_user_id:string)=>{
  try{
        const token = getAuthToken()
        const response = await axios.delete(`${API_URL}/user/${delete_user_id}`,{
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
        return response.data
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            return response.data
          }
        }
    }
}

export const getLegionExpose = async (start_date: string|null, end_date: string|null)=>{
  try{
        const token = getAuthToken() 
        const response = await axios.get(`${API_URL}/wide-area-promotion-ratio`,{
            params:{
              sdate : start_date,
              edate: end_date
            },
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
        return response.data
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            return response.data
          }
        }
    } 
}


export const getPromoterAnalysis = async (target_name:string,start_date: string|null, end_date: string|null)=>{
  try{
        const token = getAuthToken()
        const response = await axios.get(`${API_URL}/promoters-promotion-result`,{
            params:{
              target_name : target_name,
              sdate : start_date,
              edate: end_date
            },
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
        return response.data
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            return response.data
          }
        }
    } 
}

export interface getSchoolAnalysisParams{
  sdate? : string | null,
  edate? : string | null,
  wideLoc? : string | null,
  cityLoc? : string | null,
  schoolName? : string | null,
  sort_by? : string | null,
  sort_order? : string | null,
  page? : number,
  size? : number
}

export const getSchoolAnalysis = async ({sdate,edate,wideLoc,cityLoc,schoolName,sort_by,sort_order,page,size}:getSchoolAnalysisParams)=>{
  try{
        const token = getAuthToken()    
        const response = await axios.get(`${API_URL}/schools-promotion-result`,{
            params:{
                sdate : sdate,
                edate : edate,
                wideLoc : wideLoc,
                cityLoc : cityLoc,
                schoolName : schoolName,
                sort_by : sort_by,
                sort_order : sort_order,
                page : page,
                size : size
            },
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
        return response.data
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            return response.data
          }
        }
    } 
}

export const getPromoList = async ({sdate,edate,wideLoc,cityLoc,schoolName,sort_by,sort_order,page,size}:getSchoolAnalysisParams)=>{
  try{
        const token = getAuthToken()      
        const response = await axios.get(`${API_URL}/promo-events-list`,{
            params:{
                sdate : sdate,
                edate : edate,
                wideLoc : wideLoc,
                cityLoc : cityLoc,
                schoolName : schoolName,
                sort_by : sort_by,
                sort_order : sort_order,
                page : page,
                size : size
            },
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
        return response.data
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            return response.data
          }
        }
    } 
}

export interface getAnncouncementParams{
  recruitmentType : string | null
  status : string | null
  sort_by : string | null
  sort_order : string | null
  page : number
  size : number
}

export const getAnncouncement = async ({recruitmentType,status,sort_by,sort_order,page,size}:getAnncouncementParams)=>{
  try{
        const token = getAuthToken()   
        const response = await axios.get(`${API_URL}/announcements`,{
            params:{
                recruitmentType: recruitmentType,
                status: status,
                sort_by : sort_by,
                sort_order : sort_order,
                page : page,
                size : size
            },
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
        return response.data
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            return response.data
          }
        }
    } 
}

// interface etcRerutnType{
//   course_name : string
//   start_date : string
//   end_date : string
// }

interface postArmyRecruitState {
  title: string,
  recruitment_type: string,
  requirements: string,
  source_url: string,
  application_sdate: string | null,
  application_edate: string| null,
  enlistment_date: string| null,
  appointment_date: string| null,
  etc:  (string | null)[][]
}

export const postArmyRecruit = async ({title,recruitment_type,requirements,source_url,application_sdate,application_edate,enlistment_date,appointment_date,etc}:postArmyRecruitState)=>{
  try{
        const token = getAuthToken() 
        const response = await axios.post(`${API_URL}/army-recruit`,{
            title: title,
            recruitment_type: recruitment_type,
            requirements: requirements,
            source_url: source_url,
            application_sdate: application_sdate,
            application_edate: application_edate,
            enlistment_date: enlistment_date,
            appointment_date: appointment_date,
            etc: etc
        },
          {
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
        return response.data
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            return response.data
          }
        }
    } 
}

export const getAnncouncementDetail = async (announce_id : number)=>{
  try{
        const token = getAuthToken()
        const response = await axios.get(`${API_URL}/announcements/${announce_id}`,{
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
        return response.data
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            return response.data
          }
        }
    } 
}

interface patchArmyRecruitState extends postArmyRecruitState{
  announce_id :number | null
}

export const patchArmyRecruit = async ({announce_id,etc,...payload}:patchArmyRecruitState)=>{
    try{
      const transformedEtc = Array.isArray(etc)
        ? etc.map((item: any) =>
            typeof item === 'object' && item !== null && 'course_name' in item
              ? [item.course_name, item.start_date, item.end_date]
              : item // 이미 변환된 상태라면 그대로
          )
        : [];
        
        const token = getAuthToken()
        const response = await axios.patch(`${API_URL}/army-recruit/${announce_id}`,{
          ...payload,
          etc: transformedEtc
        },{
            params:{
              announce_id : announce_id
            },
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
        return response.data
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            return response.data
          }
        }
    } 
}

export const deleteAnncouncement = async (announce_id : number)=>{
  try{
        const token = getAuthToken()
        const response = await axios.delete(`${API_URL}/army-recruit/${announce_id}`,{
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
        return response.data
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            return response.data
          }
        }
    } 
}

export const getTeam = async ()=>{
  try{
        const token = getAuthToken()
        const response = await axios.get(`${API_URL}/team`,{
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
        return response.data
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            return response.data
          }
        }
    } 
}

export const getTeamMember = async (search_input:string)=>{
  try{
        const token = getAuthToken()
        const response = await axios.get(`${API_URL}/user-search`,{
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            },
            params:{
              search_input
            }
        })
        return response.data
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            return response.data
          }
        }
    } 
}

interface postCreateTeamState {
  team_name: string,
  manager_uid: number,
  member_uids: number[],
  team_areas: [string,string[]][]
}

export const postCreateTeam = async ({team_name,manager_uid,member_uids,team_areas}:postCreateTeamState)=>{
  try{
        const token = getAuthToken() 
        const payload: any = {
          team_name,
          member_uids,
          team_areas
        };
        if(manager_uid) payload.manager_uid = manager_uid
        const response = await axios.post(`${API_URL}/team-create`,payload,
          {
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
        return response
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            toast.error('팀 등록 실패',{autoClose:2000})
            return response.data
          }
        }
    } 
}

export const deleteTeam =async(team_id :number)=>{
  try{
        const token = getAuthToken()
        const response = await axios.delete(`${API_URL}/team-delete`,{
            params: {
              team_id, // ✅ 여기에 team_id 전달
            },
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            },
        })
        return response
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            toast.error('팀 삭제 실패',{autoClose:2000})
            return response.data
          }
        }
    }
}

interface patchTeamUpdateType extends postCreateTeamState{
  team_id:number
}

export const patchTeamUpdate = async ({team_name,manager_uid,member_uids,team_areas,team_id}:patchTeamUpdateType)=>{
  try{
        const token = getAuthToken() 
        const payload: any = {
          team_name,
          member_uids,
          team_areas
        };
        if(manager_uid) payload.manager_uid = manager_uid
        const response = await axios.patch(`${API_URL}/team-update`,payload,
          {
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            },
            params:{
              team_id:team_id
            }
        })
        return response
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            toast.error('팀 수정 실패',{autoClose:2000})
            return response.data
          }
        }
    } 
}

export const getTeamAnalysis = async (start_date: string|null, end_date: string|null)=>{
  try{
        const token = getAuthToken()
        const response = await axios.get(`${API_URL}/teams-promotion-result`,{
            params:{
              sdate : start_date,
              edate: end_date
            },
            headers:{
                'Content-Type' : 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
        return response.data
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            return response.data
          }
        }
    } 
}